import { KeyboardEvent, useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { CircleHelpIcon, PlusIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Chips } from '@/components/atoms/Chip/Chip';
import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { ZendeskIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { isKeyEnter } from '@/util/util';
import { zendeskTagSchema } from '@/util/validation/schemas/dialog-schemas';

import styles from './styles.module.scss';

interface Props {
  integration?: ZendeskIntegration;
  updateIntegration: (integration: ZendeskIntegration) => void;
  isLoading: boolean;
  suggestedTags?: string[];
}

const TagsFieldset = ({ isLoading, integration, updateIntegration, suggestedTags = [] }: Props) => {
  const { t } = useTranslation();
  const [tags, setTags] = useState([]);

  useEffect(() => {
    setTags(integration?.config?.tags || []);
  }, [integration?.config?.tags]);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const {
    control,
    formState: { errors, isValid },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(zendeskTagSchema),
    mode: 'onChange',
    defaultValues: { tag: '' },
  });

  const onKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (isKeyEnter(e)) {
        const tag = getValues('tag');
        if (isValid && tag) {
          setTags(uniq([...tags, tag]));
          setValue('tag', '');
        }
      }
    },
    [getValues, isValid, setValue, tags]
  );

  const handleOnTagCreate = (tag: string) => {
    setTags(uniq([...tags, tag]));
  };

  const handleOnTagDelete = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const onSubmit = useCallback(() => {
    updateIntegration({ ...integration, config: { ...integration?.config, tags } });
  }, [integration, tags, updateIntegration]);
  return (
    <FormFieldset
      title={t('common.tags')}
      subtitle={t('integrations.zendesk.tags_subtitle')}
      isLoading={false}
      primaryButton={{
        children: t('common.save'),
        disabled: isLoading || isEqual(tags, integration?.config?.tags) || !canWrite,
      }}
      onSubmit={onSubmit}
    >
      <div className={styles.tags}>
        <Label htmlFor="tag" className={cn('mb-2', !!errors.tag && 'text-warning')}>
          {t('integrations.zendesk.add_tag')}
          <InfoTooltip content={t('integrations.zendesk.type_tag')} side="right">
            <CircleHelpIcon size={12} className="text-foreground-muted" />
          </InfoTooltip>
        </Label>
        <Controller
          control={control}
          name="tag"
          render={({ field }) => (
            <Input
              id="tag"
              size="sm"
              autoComplete="off"
              autoFocus
              disabled={isLoading || tags.length > 50}
              readOnly={!canWrite}
              aria-invalid={!!errors.tag}
              onKeyUp={onKeyUp}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
        {!!errors.tag && (
          <p className="mt-1 text-sm text-warning">{t('integrations.zendesk.error_tag')}</p>
        )}
      </div>
      <div className={cn(styles.tags, { [styles.readOnly]: !canWrite })}>
        <p className="mb-1 flex items-center gap-0.5 text-xs leading-[18px] font-semibold text-foreground-muted uppercase">
          {t('common.tags')}
          <InfoTooltip content={t('integrations.zendesk.tags_to_monitor')} side="right">
            <CircleHelpIcon size={12} className="text-foreground-muted" />
          </InfoTooltip>
        </p>
        {tags.length === 0 ? (
          <p className="text-sm leading-5 font-normal">
            {t('integrations.zendesk.monitoring_all')}
          </p>
        ) : (
          <Chips chips={tags} onDelete={handleOnTagDelete} />
        )}
      </div>
      <div className={styles.tags}>
        <p className="mb-1 flex items-center gap-0.5 text-xs leading-[18px] font-semibold text-foreground-muted uppercase">
          {t('integrations.zendesk.suggested_tags')}
          <InfoTooltip content={t('integrations.zendesk.existing_tags')} side="right">
            <CircleHelpIcon size={12} className="text-foreground-muted" />
          </InfoTooltip>
        </p>
        {suggestedTags.length === 0 ? (
          <p className="text-sm leading-5 font-normal">
            {t('integrations.zendesk.tags_no_suggestions')}
          </p>
        ) : (
          <Chips chips={suggestedTags} icon={<PlusIcon />} onDelete={handleOnTagCreate} />
        )}
      </div>
    </FormFieldset>
  );
};
export default TagsFieldset;
